import React from "react";
import { useNetworkContext } from "../../context/NetworkContext";
import { getEntries, syncOfflineEntries } from "../../web/utils/offlineDB";
import "./NetworkStatusPill.css";

function NetworkStatusPill({ iconOnly }) {
  const isOnline = useNetworkContext();
  const [pendingCount, setPendingCount] = React.useState(0);

  React.useEffect(() => {
    const updateCount = async () => {
      try {
        const entries = await getEntries();
        const pending = entries.filter(
          (e) => e.status === "queued" || e.status === "failed" || e.status === "syncing"
        );
        setPendingCount(pending.length);
      } catch (err) {
        console.error("Error reading offline entries for status pill:", err);
      }
    };

    updateCount();

    window.addEventListener("offline-db-updated", updateCount);
    return () => window.removeEventListener("offline-db-updated", updateCount);
  }, []);

  React.useEffect(() => {
    if (isOnline && pendingCount > 0) {
      syncOfflineEntries();
    }
  }, [isOnline, pendingCount]);

  const isSyncing = isOnline && pendingCount > 0;

  let pillClass = "network-pill--online";
  if (!isOnline) {
    pillClass = "network-pill--offline";
  } else if (isSyncing) {
    pillClass = "network-pill--syncing";
  }

  let labelText = "Online";
  if (!isOnline) {
    labelText = pendingCount > 0 ? `Offline • ${pendingCount} Pending` : "Offline";
  } else if (isSyncing) {
    labelText = `Syncing • ${pendingCount} Pending`;
  }

  return (
    <div className={`network-pill ${pillClass} ${iconOnly ? "network-pill--icon-only" : ""}`}>
      {isSyncing ? (
        <svg
          className="network-pill__icon network-pill__icon--spinning"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
        </svg>
      ) : (
        <svg
          className="network-pill__icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {isOnline ? (
            <>
              <path d="M5 12.55a11 11 0 0 1 14.08 0" />
              <path d="M1.42 9a16 16 0 0 1 21.16 0" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <circle cx="12" cy="20" r="1" fill="currentColor" />
            </>
          ) : (
            <>
              <line x1="1" y1="1" x2="23" y2="23" />
              <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
              <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
              <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
              <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <circle cx="12" cy="20" r="1" fill="currentColor" />
            </>
          )}
        </svg>
      )}
      {!iconOnly && <span className="network-pill__label">{labelText}</span>}
    </div>
  );
}

export default NetworkStatusPill;
